import { Component, EventEmitter, Input, Output, ViewChild } from '@angular/core';
import { MatButtonToggleChange } from '@angular/material/button-toggle';
import {MenuBackup, MenuSection} from 'app/models/menu.model';
import { MatMenuTrigger } from '@angular/material/menu';
import { Clipboard } from '@angular/cdk/clipboard';
import { SnackbarService } from 'app/services/snackbar.service';
import { TranslateService } from '@ngx-translate/core';
import { UntilDestroy } from '@ngneat/until-destroy';
import { RoleMembership } from "app/reducers/context.selectors";
import { MatDialog } from '@angular/material/dialog';
import { MenuSchedulesModalComponent } from 'app/pages/menu/shared/menu-schedules-modal/menu-schedules-modal.component';

@UntilDestroy()
@Component({
  selector: 'app-menu-chips[roleMembership][isShowDeleted]',
  templateUrl: './menu-chips.component.html',
  styleUrls: ['./menu-chips.component.scss']
})
export class MenuChipsComponent {
  @Input() selectedMenuSectionId: string;
  @Input() menuSections: MenuSection[];
  @Input() menuBackups: MenuBackup[];
  @Input() roleMembership: RoleMembership;
  @Input() isShowDeleted: boolean;

  @Output() editMenu = new EventEmitter<MenuSection | null>();
  @Output() deleteMenu = new EventEmitter<MenuSection | null>();
  @Output() restoreMenu = new EventEmitter<MenuSection | null>();
  @Output() addNewMenu = new EventEmitter<string | null>();
  @Output() menuSectionSelected = new EventEmitter<string>();
  @Output() createMenuBackup = new EventEmitter<string>();
  @Output() deleteBackup = new EventEmitter<string>();
  @Output() downloadMenu = new EventEmitter<string>();
  @Output() migrateRelatedProductsLegacyToV2 = new EventEmitter<MenuSection>();
  @Output() migrateRelatedProductsV2ToV3 = new EventEmitter<MenuSection>();

  selectedId:string = null;

  contextMenuPosition = {x: '0px', y: '0px'};

  @ViewChild(MatMenuTrigger)
  contextMenu: MatMenuTrigger;

  constructor(
    private clipboard: Clipboard,
    private snackbar: SnackbarService,
    private translate: TranslateService,
    private dialog: MatDialog,
  ) {
  }

  onAddNewMenu(): void {
    this.addNewMenu.emit();
  }

  onSelected(event: MatButtonToggleChange): void {
    this.selectedId = event.value;
    this.menuSectionSelected.emit(event.value);
  }

  onSelectedBackup(event: MatButtonToggleChange): void {
    this.selectedMenuSectionId = null;
  }

  onContextMenu(event: MouseEvent, menuSection?: MenuSection, menuBackup?: MenuBackup): void {
    event.preventDefault();
    this.contextMenuPosition.x = event.clientX + 'px';
    this.contextMenuPosition.y = event.clientY + 'px';
    this.contextMenu.menuData = menuSection ? {menuSection} : {menuBackup};
    this.contextMenu.menu.focusFirstItem('mouse');
    this.contextMenu.openMenu();
  }

  isMenuSectionContext() : boolean{
    return !!this.contextMenu.menuData.menuSection;
  }

  onCopyIdClick(menuSection: MenuSection): void {
    if(this.isMenuSectionContext()){
      this.clipboard.copy(this.contextMenu.menuData.menuSection.id);
      this.snackbar.info(this.translate.instant('MENU.ID_COPIED', {name: this.contextMenu.menuData.menuSection.name}));
    }
    else {
      this.clipboard.copy(this.contextMenu.menuData.menuBackup.id);
      this.snackbar.info(this.translate.instant('MENU.ID_COPIED', {name: this.contextMenu.menuData.menuBackup.name}));
    }
  }

  onCopyMenuClick(menuSection: MenuSection): void {
    this.addNewMenu.emit(this.contextMenu.menuData.menuSection.id);
  }

  onDeleteMenuClick(menuSection: MenuSection): void {
    if(this.isMenuSectionContext()){
      this.deleteMenu.emit(this.contextMenu.menuData.menuSection);
    } else {
      this.deleteBackup.emit(this.contextMenu.menuData.menuBackup.id);
    }
  }

  onRestoreMenuClick(menuSection: MenuSection): void {
    this.restoreMenu.emit(this.contextMenu.menuData.menuSection);
  }

  onEdit(menuSection: MenuSection): void {
    this.editMenu.emit(this.contextMenu.menuData.menuSection);
  }

  isDeleted(menuSection: MenuSection): boolean {
    return this.isMenuSectionContext() && this.contextMenu.menuData.menuSection.isDeleted;
  }

  switchShowDeleted() {
    this.isShowDeleted = !this.isShowDeleted;
  }

  onClickSchedules(menuSection: MenuSection){
    this.dialog.open(
      MenuSchedulesModalComponent,
      {
        minWidth: '50%',
        data: this.contextMenu.menuData.menuSection.id,
      });
  }

  onClickMigrateLegacyToV2(): void {
    this.migrateRelatedProductsLegacyToV2.emit(this.contextMenu.menuData.menuSection);
  }

  onClickMigrateV2ToV3(): void {
    this.migrateRelatedProductsV2ToV3.emit(this.contextMenu.menuData.menuSection);
  }

  createBackup() {
    const item = this.contextMenu.menuData.menuSection;

    this.createMenuBackup.emit(item.id);
  }

  downloadMenuByName(){
    this.downloadMenu.emit(this.contextMenu.menuData.menuBackup.id);
  }

  getCurrentBackupInfo(){
    const currentBackup = this.menuBackups?.find(b => b.id == this.selectedId);
    const menuSection = currentBackup ? this.menuSections.find(m => m.id == currentBackup.basedMenuSectionId) : null;

    const info: string[] = currentBackup ? [
      'Backup',
      'created at: ' + currentBackup?.createdAt,
      'based on ' + menuSection?.name,
    ] : null;


    return info;
  }
}
